import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Check, X, Zap, Shield, Crown, Building2, Loader2, CreditCard, ArrowRight, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tierIcons = { trial: Shield, premium: Zap, ai_pro: Crown, enterprise: Building2 };

export default function PricingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [annual, setAnnual] = useState(true);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(false);

  useEffect(() => {
    fetchPricing();
    if (user) fetchCurrentSubscription();
  }, [user]);

  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan && user && plans.length > 0 && !checkingOut) {
      setAnnual(searchParams.get('cycle') !== 'monthly');
      window.history.replaceState({}, '', '/pricing');
      initiateCheckout(plan);
    }
  }, [searchParams, user, plans]);

  // Check for payment success/cancel from URL params
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled');
    }
  }, [searchParams]);

  const fetchPricing = async () => {
    try { 
      const res = await axios.get(`${API}/pricing`); 
      setPlans(res.data.plans); 
    } catch {} 
    finally { setLoading(false); }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/current`, { withCredentials: true });
      setCurrentSubscription(res.data.subscription);
    } catch {}
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your subscription status.');
      setPollingStatus(false);
      return;
    }
    
    setPollingStatus(true);
    
    try {
      const res = await axios.get(`${API}/subscriptions/status/${sessionId}`, { withCredentials: true });
      
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        fetchCurrentSubscription();
        setPollingStatus(false);
        // Clean URL
        window.history.replaceState({}, '', '/pricing');
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setPollingStatus(false);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const initiateCheckout = async (planId) => {
    if (planId === 'enterprise') {
      toast.info('Please contact sales for Enterprise pricing');
      return;
    }
    if (!user) {
      toast.info('Create an account to complete your purchase');
      navigate(`/login?mode=register&next=${encodeURIComponent(`/pricing?plan=${planId}&cycle=${annual ? 'yearly' : 'monthly'}`)}`);
      return;
    }
    
    setCheckingOut(planId);
    
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: planId,
        billing_cycle: annual ? 'yearly' : 'monthly',
        origin_url: originUrl,
      }, { withCredentials: true });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate checkout');
    } finally {
      setCheckingOut(null);
    }
  };

  const getPrice = (plan) => {
    if (plan.custom_pricing) {
      return { price: 'Custom', period: '', total: 'Starting $20,000+/year', isCustom: true };
    }
    if (annual) {
      const monthlyEquivalent = Math.round(plan.yearly / 12);
      return { price: monthlyEquivalent, period: '/mo', total: `$${plan.yearly.toLocaleString()}/year`, savings: Math.round((1 - plan.yearly / (plan.monthly * 12)) * 100) };
    }
    return { price: plan.monthly, period: '/mo', total: null };
  };

  const isCurrentPlan = (planId) => {
    return currentSubscription?.plan_id === planId && currentSubscription?.status === 'active';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto">
        <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Pricing</Badge>
        <h1 className="text-3xl md:text-4xl font-bold font-heading tracking-tight" data-testid="pricing-title">
          Choose Your Security Plan
        </h1>
        <p className="text-base text-muted-foreground mt-3 max-w-2xl mx-auto">
          Enterprise-grade AI-powered security for teams of all sizes. Scale your security posture with Vaptara.
        </p>
        
        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mt-8 p-2 rounded-xl bg-muted/30 w-fit mx-auto">
          <span className={`text-sm px-3 py-1.5 rounded-lg transition-all ${!annual ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`}>Monthly</span>
          <Switch checked={annual} onCheckedChange={setAnnual} data-testid="billing-toggle" />
          <span className={`text-sm px-3 py-1.5 rounded-lg transition-all ${annual ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`}>
            Annual
            <Badge className="ml-2 bg-green-500/10 text-green-500 border-green-500/20 text-[10px]">Save up to 30%</Badge>
          </span>
        </div>
      </div>

      {/* Payment Status Polling */}
      {pollingStatus && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="w-[400px]">
            <CardContent className="pt-6 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Verifying Payment</h3>
              <p className="text-sm text-muted-foreground">Please wait while we confirm your subscription...</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Current Subscription Banner */}
      {currentSubscription?.status === 'active' && (
        <div className="max-w-5xl mx-auto">
          <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">Current Plan: <span className="text-primary capitalize">{currentSubscription.plan_id}</span></p>
                <p className="text-xs text-muted-foreground">
                  {currentSubscription.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'} billing • 
                  Renews {new Date(currentSubscription.ends_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="border-green-500/30 text-green-500">Active</Badge>
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto px-4">
        {plans.map(plan => {
          const Icon = tierIcons[plan.id] || Shield;
          const pricing = getPrice(plan);
          const isCurrent = isCurrentPlan(plan.id);
          
          return (
            <Card key={plan.id} 
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col ${
                plan.popular ? 'border-primary shadow-[0_0_30px_rgba(59,130,246,0.15)] scale-[1.02] z-10' : 'border-border'
              } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
              data-testid={`plan-${plan.id}`}>
              
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: plan.color }} />
              )}
              
              {/* Current Plan Badge */}
              {isCurrent && (
                <div className="absolute top-3 right-3">
                  <Badge className="bg-green-500 text-white text-[10px]">Current</Badge>
                </div>
              )}
              
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-lg" style={{ background: `${plan.color}15` }}>
                    <Icon className="h-5 w-5" style={{ color: plan.color }} />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-heading">{plan.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{plan.subtitle}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{plan.target}</p>
              </CardHeader>
              
              <CardContent className="space-y-5 flex-1 flex flex-col">
                {/* Price */}
                <div>
                  <div className="flex items-end gap-1">
                    {pricing.isCustom ? (
                      <span className="text-3xl font-bold font-heading" style={{ color: plan.color }}>Custom</span>
                    ) : (
                      <>
                        <span className="text-3xl font-bold font-heading" style={{ color: plan.color }}>${pricing.price}</span>
                        <span className="text-sm text-muted-foreground mb-1">{pricing.period}</span>
                      </>
                    )}
                  </div>
                  {pricing.total && <p className="text-xs text-muted-foreground mt-1">{pricing.total}</p>}
                  {pricing.savings > 0 && <Badge className="mt-1 bg-green-500/10 text-green-500 border-green-500/20 text-[10px]">Save {pricing.savings}%</Badge>}
                </div>

                {/* Limits */}
                {!plan.custom_pricing && (
                  <div className="flex gap-2 text-xs text-muted-foreground flex-wrap">
                    <span className="px-2 py-1 rounded bg-muted/50">{plan.scan_credits === -1 ? 'Unlimited' : plan.scan_credits.toLocaleString()} credits</span>
                    <span className="px-2 py-1 rounded bg-muted/50">{plan.projects === -1 ? '∞' : plan.projects} projects</span>
                    <span className="px-2 py-1 rounded bg-muted/50">{plan.users === -1 ? '∞' : plan.users} users</span>
                  </div>
                )}

                {/* CTA Button */}
                <Button 
                  className="w-full gap-2" 
                  variant={plan.popular ? 'default' : 'outline'}
                  disabled={checkingOut === plan.id || isCurrent}
                  onClick={() => initiateCheckout(plan.id)}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {checkingOut === plan.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isCurrent ? (
                    'Current Plan'
                  ) : plan.id === 'enterprise' ? (
                    <>Contact Sales <ArrowRight className="h-4 w-4" /></>
                  ) : (
                    <>Get Started <ArrowRight className="h-4 w-4" /></>
                  )}
                </Button>

                <Separator />

                {/* Features */}
                <div className="space-y-2 flex-1">
                  {plan.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <Check className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: plan.color }} />
                      <span>{f}</span>
                    </div>
                  ))}
                  {plan.excluded?.map((f, i) => (
                    <div key={`ex-${i}`} className="flex items-start gap-2 text-xs text-muted-foreground/50">
                      <X className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span className="line-through">{f}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Enterprise CTA */}
      <div className="max-w-4xl mx-auto px-4">
        <Card className="bg-gradient-to-br from-primary/5 to-purple-500/5 border-primary/20">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="p-4 rounded-2xl bg-primary/10">
                <Building2 className="h-10 w-10 text-primary" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold font-heading mb-2">Need a Custom Solution?</h3>
                <p className="text-sm text-muted-foreground">
                  For large enterprises, regulated industries, and custom deployment needs, our team will work with you to create a tailored security solution.
                </p>
              </div>
              <Button size="lg" className="gap-2" data-testid="contact-sales-btn">
                <Sparkles className="h-4 w-4" /> Contact Sales
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* FAQ or Trust Badges */}
      <div className="max-w-3xl mx-auto text-center pb-8">
        <p className="text-xs text-muted-foreground">
          All plans include 14-day free trial • No credit card required for trial • Cancel anytime
        </p>
        <div className="flex items-center justify-center gap-6 mt-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Shield className="h-3.5 w-3.5" /> SOC 2 Compliant</span>
          <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5" /> GDPR Ready</span>
          <span className="flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" /> Secure Payments</span>
        </div>
      </div>
    </div>
  );
}
